import React, { useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';

const SiteApproverReview = ({ investigation, onApprove, onReject, currentRole }) => {
  const [comment, setComment] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const canAct = currentRole === 'approver' && investigation?.status === 'findings-documented';
  const isApproved = ['approved', 'closed', 'completed']?.includes(investigation?.status);

  const approvalHistory = investigation?.approvalHistory || [];

  const handleApprove = () => {
    onApprove?.({ comment });
    setComment('');
  };

  const handleReject = () => {
    if (!rejectReason?.trim()) return;
    onReject?.({ reason: rejectReason });
    setRejectReason('');
    setShowRejectForm(false);
  };

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center gap-2 mb-5">
        <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
          <Icon name="ShieldCheck" size={18} className="text-teal-600 dark:text-teal-400" />
        </div>
        <h3 className="text-base font-semibold text-foreground">Site Approver Review</h3>
      </div>

      {/* Current Status */}
      <div className="mb-4 p-4 rounded-lg border border-border bg-muted/30">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">Investigation Status</span>
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
            isApproved ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
            investigation?.status === 'findings-documented'? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
          }`}>
            {isApproved ? 'Approved' : investigation?.status === 'findings-documented' ? 'Pending Review' : 'In Progress'}
          </span>
        </div>
        {isApproved && (
          <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
            <Icon name="CheckCircle" size={15} />
            <span>Investigation approved — recommendations can now be raised</span>
          </div>
        )}
        {investigation?.status === 'findings-documented' && (
          <div className="flex items-center gap-2 text-sm text-purple-600 dark:text-purple-400">
            <Icon name="Clock" size={15} />
            <span>Awaiting site approver review</span>
          </div>
        )}
        {!['findings-documented', 'approved', 'closed', 'completed']?.includes(investigation?.status) && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Info" size={15} />
            <span>Investigation must be submitted before review</span>
          </div>
        )}
      </div>

      {/* Approval Actions */}
      {canAct && (
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Review Comments (optional)</label>
            <textarea
              placeholder="Add any review notes or conditions..."
              value={comment}
              onChange={e => setComment(e?.target?.value)}
              rows={3}
              className="w-full text-sm px-3 py-2.5 bg-background border border-border rounded-lg outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground resize-none"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleApprove}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Icon name="CheckCircle" size={16} />
              Approve Investigation
            </button>
            <button
              onClick={() => setShowRejectForm(!showRejectForm)}
              className="flex items-center justify-center gap-2 px-4 py-2.5 border border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 text-sm font-medium rounded-lg transition-colors"
            >
              <Icon name="XCircle" size={16} />
              Reject
            </button>
          </div>
          {showRejectForm && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg space-y-2">
              <label className="text-xs font-medium text-red-700 dark:text-red-400 block">Rejection Reason *</label>
              <textarea
                placeholder="Explain what needs to be revised before re-submission..."
                value={rejectReason}
                onChange={e => setRejectReason(e?.target?.value)}
                rows={2}
                className="w-full text-sm px-3 py-2 bg-background border border-red-200 dark:border-red-800 rounded-lg outline-none focus:ring-2 focus:ring-red-300 text-foreground placeholder:text-muted-foreground resize-none"
              />
              <button
                onClick={handleReject}
                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-lg transition-colors"
              >
                Confirm Rejection
              </button>
            </div>
          )}
        </div>
      )}

      {/* Approval History */}
      {approvalHistory?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Approval History</h4>
          <div className="space-y-2">
            {approvalHistory?.map((entry, idx) => (
              <div key={idx} className="flex items-start gap-3 text-sm">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  entry?.action === 'approved' ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'
                }`}>
                  <Icon name={entry?.action === 'approved' ? 'Check' : 'X'} size={11} />
                </div>
                <div>
                  <p className="font-medium text-foreground capitalize">{entry?.action} by {entry?.by}</p>
                  {entry?.comment && <p className="text-muted-foreground text-xs mt-0.5">{entry?.comment}</p>}
                  <p className="text-xs text-muted-foreground mt-0.5">{entry?.at ? format(new Date(entry.at), 'MMM dd, yyyy HH:mm') : ''}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteApproverReview;
